import i18n from "i18next";
import {Consts} from "./consts";
import {phenotypeMap, strengthUnits} from "../../resources/json/jsons";

/**
 * Formats date strings like "2026-04-20T15:22:27.341Z" (iso) or "2026-04-20 15:22:27"
 * to "dd.MM.yyyy HH:mm" or to "dd/MM/yyyy HH:mm" if the time should be shown
 * or to "dd.MM.yyyy" or to "dd/MM/yyyy" if the time should be NOT shown
 *
 * @param {string} input
 * @param {boolean} onlyDate
 * @returns {string|null} e.g."20.04.2026 15:22" or null for not valid date
 */
export function formatDateTime(input, onlyDate) {
  if (typeof input !== "string") {
    return null;
  }

  let s = input.trim();

  // convert to iso: "2026-04-20 15:22:27" -> "2026-04-20T15:22:27"
  if (s.includes(" ") && !s.includes("T")) {
    s = s.replace(" ", "T");
  }

  const d = new Date(s);

  if (isNaN(d.getTime())) {
    return null;
  }

  const pad = n => String(n).padStart(2, "0");

  // use getDate/getHours if we always want to have the local time
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  const hour = pad(d.getHours());
  const minute = pad(d.getMinutes());
  const sign = i18n.language === "nl" ? "/" : "."

  return onlyDate
    ? `${day}${sign}${month}${sign}${year}`
    : `${day}${sign}${month}${sign}${year} ${hour}:${minute}`;
}

export function parseDate(input) {
  if (input instanceof Date) {
    return input;
  }

  const dateRegex1 = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/; // yyyy-mm-dd or yyyy-mm-ddTHH:MM:SS
  const dateRegex2 = /^\d{2}\/\d{2}\/\d{4}(?: \d{2}:\d{2})?$/; // dd/mm/yyyy or dd/mm/yyyy HH:MM

  if (dateRegex1.test(input)) {
    return new Date(input);
  } else if (dateRegex2.test(input)) {
    const [datePart, timePart] = input?.split(" ");
    const [day, month, year] = datePart?.split("/")?.map(Number);
    const hours = timePart ? Number(timePart?.split(":")[0]) : 0;
    const minutes = timePart ? Number(timePart?.split(":")[1]) : 0;
    return new Date(year, month - 1, day, hours, minutes);
  }

  return null;
}

export function buildMeasurement(name, value, unit) {
  return {
    name,
    comment: Consts.ANY,
    dateTime: (new Date()).toString(),
    params: [{value, unit, name}],
  };
}

export const buildRisks = (dpwgDrugs, riskKind) => {
  return dpwgDrugs?.map((dpwgDrug) => {
    const substance = dpwgDrug[i18n?.language?.toUpperCase()]?.substanceName || dpwgDrug?.substanceKey;

    return {
      activeSubstance: substance,
      recommendation: JSON.stringify(dpwgDrug),
      substanceKey: dpwgDrug?.substanceKey,
      description: riskKind,
    };
  });
};

const handleAndersGenes = (
  geneName,
  genotype,
  phenotype,
  genotypeSubstances,
  phenotypeSubstances,
) => {
  const isCYP2C9 = geneName === Consts.ANDERS_GENES[0];
  const isIntermediateMetabolizer = phenotypeMap[phenotype] === Consts.INTERMEDIATE_METABOLIZER;
  const isPoorMetabolizer = phenotypeMap[phenotype] === Consts.POOR_METABOLIZER;
  let showPheno;

  if(isCYP2C9){
    showPheno = (isIntermediateMetabolizer && !Consts.NOT_CYP2C9_INTERMEDIATE_GENOS?.includes(genotype))
      || (isPoorMetabolizer && !Consts.NOT_CYP2C9_POOR_GENOS?.includes(genotype));
  } else {
    showPheno = (isIntermediateMetabolizer && !Consts.NOT_UGT1A1_GENOS?.includes(genotype))
      || (isPoorMetabolizer && !Consts.NOT_UGT1A1_GENOS?.includes(genotype));
  }

  return {
    risks: [],
    genoRisks: showPheno ? [] : buildRisks(genotypeSubstances, Consts.GENO_RISKS),
    phenoRisks: showPheno ? buildRisks(phenotypeSubstances, Consts.PHENO_RISKS) : [],
  };
};

const handleGenes = (
  genotype,
  phenotype,
  phenotypeSubstances,
  genotypeSubstances,
) => {
  let risks = [];
  let genoRisks = [];
  let phenoRisks = [];

  if (genotypeSubstances?.length > 0 && phenotypeSubstances?.length > 0) {
    risks = buildRisks(genotypeSubstances, Consts.COMPLETE_RISKS);
  } else if (phenotypeSubstances?.length > 0) {
    phenoRisks = buildRisks(phenotypeSubstances, Consts.PHENO_RISKS);
  } else if ((!phenotypeSubstances || phenotypeSubstances?.length === 0) && genotypeSubstances?.length > 0) {
    genoRisks = buildRisks(genotypeSubstances, Consts.GENO_RISKS);
  }

  return { risks, genoRisks, phenoRisks };
};

export const buildRiskObject = (
  phenotype,
  genotype,
  geneFromJson,
) => {
  const fullGenotypeRisks = geneFromJson?.genotypes?.find(geno => geno?.genotypeName === genotype);
  const genotypeSubstances = fullGenotypeRisks?.activeSubstances ? fullGenotypeRisks.activeSubstances : [];
  const alleles = genotype?.split("/");

  const genotypeAlleleRisks = alleles?.map(allele =>
    geneFromJson?.genotypes?.find(geno => geno?.genotypeName === allele)
  );

  genotypeAlleleRisks?.forEach(geno => {
    if (geno?.activeSubstances) {
      genotypeSubstances?.push(...geno?.activeSubstances);
    }
  });

  const phenotypeRisks = geneFromJson?.phenotypes?.find(pheno => pheno?.phenotypeName === phenotypeMap[phenotype]);

  if (Consts.ANDERS_GENES?.includes(geneFromJson?.geneName)) {
    return handleAndersGenes(geneFromJson?.geneName, genotype, phenotype, genotypeSubstances,
      phenotypeRisks?.activeSubstances);
  } else {
    return handleGenes(genotype, phenotype, phenotypeRisks?.activeSubstances, genotypeSubstances);
  }
};

export const buildInteractionParam = (param, withDescription) => {
  let newParam = param;

  if (param?.includes("EN")) {
    const parsed = JSON.parse(param);
    newParam = parsed[i18n.language?.toUpperCase()];

    if (withDescription) {
      newParam = newParam?.description;
    }
  }

  return newParam;
}

export const isOnlyNumber = (str) => /^\d+$/.test(str);

export const sortData = (key, items, ascending, getValue) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const valueGetter =
    typeof getValue === "function" ? getValue : (item) => item?.[key];

  return [...items]?.sort((a, b) => {
    const valueA = valueGetter(a);
    const valueB = valueGetter(b);

    if (valueA == null && valueB == null) {
      return 0;
    }

    if (valueA == null) {
      return ascending ? -1 : 1;
    }

    if (valueB == null) {
      return ascending ? 1 : -1;
    }

    const dateA = parseDate(valueA);
    const dateB = parseDate(valueB);

    if (dateA && dateB) {
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();

      if (timeA === timeB) {
        return 0;
      }

      return ascending ? (timeA > timeB ? 1 : -1) : (timeA < timeB ? 1 : -1);
    }

    const normalizedA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
    const normalizedB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;

    if (normalizedA === normalizedB) {
      return 0;
    }

    return ascending ? (normalizedA > normalizedB ? 1 : -1) : (normalizedA < normalizedB ? 1 : -1);
  });
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0)?.toUpperCase() + string?.slice(1);
}

export const filterIcd10Data = (data) => {
  return data?.filter(item => {
    if(item?.icd10Name[i18n.language?.toUpperCase()] !== ""){
      return item;
    }
  });
}

export const removeLastSign = (text, sign) => {
  let newText = text;

  if(newText?.endsWith(sign)){
    newText = newText?.trim()?.slice(0, -1);
  }

  return newText;
}

export function removeDdiDuplicates(array) {
  const uniqueItems = [];
  const seen = new Map();

  array?.forEach(item => {
    const key = JSON.stringify({
      ddiDrug: item?.ddiDrug?.compound,
      focusDrug: item?.focusDrug?.compound
    });

    if (seen?.has(key)) {
      const existingItem = seen?.get(key);

      if (item?.relevance?.abdataCode > existingItem?.relevance?.abdataCode) {
        seen?.set(key, item);
      }
    } else {
      seen?.set(key, item);
    }
  });

  seen?.forEach(value => uniqueItems?.push(value));
  return uniqueItems;
}

export const extractDrugStrengthInfo = (productName) => {
  const strengthExtracted = extractStrength(productName);

  const strengthUnit = strengthExtracted?.unit !== ""
    ? strengthUnits?.filter(
      (option) => option?.value?.strengthUnit === strengthExtracted?.unit,
    )?.[0]?.value
    : "";

  return [strengthExtracted?.strength, strengthUnit];
};

const extractStrength = (drugName) => {
  const regex = /(\d+[,.]?\d*)\s*(mg|g|ml|μg|l)/i;
  const match = drugName?.match(regex);
  let strength = match ? match[1]?.replace(",", ".") : 0;
  strength = isNaN(strength) ? 0 : Number(strength);
  const unit = match ? match[2] : "";
  return {strength, unit};
};

export const checkDrugForDoubleEntry = (drugs, newProduct) => {
  const productsMatched = drugs?.filter((drug) => drug?.productName === newProduct);
  return productsMatched?.length > 0;
};

export const isSlovenia = (hospital) => {
  return hospital === "Zdravstveni center Ljubljana - VIČ"
    || hospital === "Zdravstveni center Ljubljana - BEŽIGRAD"
    || hospital === "Ambulanta KUS"
    || hospital === "ZZ Zdravje"
    || hospital === "Psychiatric Clinic BJS"
    || hospital === "University Psichiatric Clinic Ljubljana";
}

export const getPatientName = (patient, disabledSeeNames) => {
  const id = patient?.id;
  const first = patient?.first_name?.trim();
  const last = patient?.last_name?.trim();
  const pseudonym = patient?.pseudonym?.trim();
  const login = patient?.login?.trim();

  if (disabledSeeNames && !patient?.delete_requested) {
    return id ?? login ?? pseudonym ?? "";
  }

  if (last) {
    return first ? `${first} ${last}` : last;
  }

  return pseudonym || login || "";
};

export const toYearMonthDay = (d) => d ? new Date(d).toISOString().slice(0, 10) : "";

export const getInputFocusStyle = (isFocused) => ({
  caretColor: '#0078d4',
  borderColor: isFocused ? '#0078d4' : undefined,
  boxShadow: isFocused ? '0 0 0 2px rgba(0, 120, 212, 0.2)' : undefined,
  outline: 'none'
});

export const extendSymptoms = (arr1, arr2) => {
  return arr2.map(item2 => {
    const match = arr1.find(item1 =>
      item1.type === item2.type &&
      item1.term === item2.term &&
      item1.sufferingLevel === item2.sufferingLevel &&
      item1.comment === item2.comment
    );

    return {
      ...item2,
      ...(match && {
        number: match.number,
        files: match.files
      }),
      endDate: item2.deactivated === 0 ? "" : item2.endDate
    };
  });
}

export function calcDeviation(values, entriesLength) {
  // Example: I have an array with the length = 8: [2, 4, 4, 4, 5, 5, 7, 9]
  // 1. find average value: 2 + 4 + 4 + 4 + 5 + 5 + 7 + 9 = 40; 40 / 8 = 5 - average
  const avg = values.reduce((a, b) => a + Number(b), 0) / entriesLength;

  // 2. find difference of each value and the average value: val - avg
  // 3. find power of two for each difference: Math.pow(val - avg, 2)
  // 4. find the variance: it is the average of the sums of all powers of two
  const variance = values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / entriesLength;

  // 5. find the standard deviation: it is the sqrt of the variance
  return Math.round(Math.sqrt(variance));
}

//  If the data points are plotted on a timeline, the standard deviation indicates how widely the dates are scattered around the average date.
export function calcDatesDeviation(dates, entriesLength, isActive) {
  /*const dates = [
  new Date("2025-01-01"),
  new Date("2025-01-03"),
  new Date("2025-01-05")
  ];*/
  const calcLength = isActive ? dates.length : entriesLength;
  const timestamps = dates.map(date => date.getTime());
  const avg = timestamps.reduce((a, b) => a + Number(b), 0) / calcLength;

  const variance = timestamps.reduce((sum, value) => {
      return sum + (value - avg) ** 2;
    }, 0) / calcLength;

  const stdDev = Math.sqrt(variance);
  return Math.round(stdDev / (1000 * 60 * 60 * 24)); // deviation in days: 1000 * 60 * 60 * 24 are millisPerDay
}

/* If we want to calculate the standard deviation of the time intervals between the date values
    (which is often more interesting), we first calculate the differences between each pair of consecutive
    date values and then use those to calculate the standard deviation. This is a different statistical measure.
 */
export function calcDatesDeviation2(dates, entriesLength, isActive) {
  /*
  E.g.
01.01.2025
03.01.2025  -> difference: 2 days
08.01.2025  -> difference: 5 days
10.01.2025  -> difference: 2 days
   */
  if (dates.length < 2) {
    return 0;
  }

  const calcLength = isActive ? dates.length : entriesLength;
  const sortedDates = [...dates].sort((a, b) => a - b); // sort by date
  const intervals = []; // Array for time differences in milliseconds

  for (let i = 1; i < calcLength; i++) {
    intervals.push(sortedDates[i].getTime() - sortedDates[i - 1].getTime());
  }

  const avg = intervals.reduce((sum, value) => sum + Number(value), 0) / calcLength;   // Average value
  const variance = intervals.reduce((sum, value) => sum + (value - avg) ** 2, 0) / calcLength;

  const stdDevMs = Math.sqrt(variance); // in milliseconds
  return stdDevMs / (1000 * 60 * 60 * 24); // in days
  // e.g. result 1.4142135623730951 means that on average, the intervals between appointments vary
  // by about 1.41 days around their mean interval.
}

export const formatDateToDayMonthYear = (date, delim) => {
  return String(date.getDate()).padStart(2, '0') + delim
    + String(date.getMonth() + 1).padStart(2, '0') + delim + date.getFullYear();
}

export const getMinMaxDates = (dates) => {
  if(dates?.length === 0){
    return [];
  }

  const min = new Date(Math.min(...dates));
  const max = new Date(Math.max(...dates));
  const minStr = formatDateToDayMonthYear (min, '.');  // e.g. "15.11.2025"
  const maxStr = formatDateToDayMonthYear (max, '.');  // e.g. "15.11.2025"
  return [minStr, maxStr];
}

export const getGenderStatistics = (rowKey, numberPatients, malePatients, femalePatients) => {
  if (rowKey === "min" || rowKey === "max" || rowKey === "avg" || rowKey === "avgActive" || rowKey === "deviation"
    || rowKey === "deviationActive") {
    return "-";
  } else if(rowKey === "totalPats"){
    return numberPatients;
  } else if(rowKey === "malePats"){
    return malePatients;
  } else if(rowKey === "femalePats"){
    return femalePatients;
  }
}

export const getYearOfBirthStatistics = (rowKey, yearsOfBirth, entriesLength) => {
  if(rowKey === "min"){
    return Math.min(...yearsOfBirth);
  } else if(rowKey === "max"){
    return Math.max(...yearsOfBirth);
  } else if(rowKey === "avg" || rowKey === "avgActive" || rowKey === "deviation" || rowKey === "deviationActive") {
    const calcLength = rowKey === "avg" || rowKey === "deviation" ? entriesLength : yearsOfBirth.length;

    if(rowKey === "avg" || rowKey === "avgActive"){
      return Math.trunc((yearsOfBirth.reduce((sum, elem) => sum + Number(elem), 0)) / calcLength);
    } else {
      return calcDeviation(yearsOfBirth, entriesLength, rowKey === "deviationActive");
    }
  } else {
    return '-';
  }
}

export const getAvgAndDeviationsForDates = (dates, rowKey, entriesLength) => {
  const timestamps = dates.map(date => date.getTime());
  const calcLength = (rowKey === "avg" || rowKey === "deviation") ? entriesLength : timestamps.length;

  if(rowKey === "avg" || rowKey === "avgActive"){
    const avg = timestamps.reduce((a, b) => a + Number(b), 0) / calcLength;
    const date = new Date(avg);

    return String(date.getDate()).padStart(2, '0') + '.'
      + String(date.getMonth() + 1).padStart(2, '0') + '.' + date.getFullYear();
  } else {
    return calcDatesDeviation(dates, entriesLength, rowKey === "deviationActive");
  }
}

export const getAvgAndDeviationsForNumbers = (numbers, rowKey) => {
  const noZeroNumbers = numbers.filter(x => x !== 0); // without 0 values
  const calcLength = rowKey === "avg" || rowKey === "deviation" ? numbers.length : noZeroNumbers?.length;

  return rowKey === "avg" || rowKey === "avgActive"
    ? Math.trunc(numbers.reduce((sum, elem) => sum + Number(elem), 0) / calcLength)
    : calcDeviation(numbers, calcLength);
}

export const getNumberStatistics = (rowKey, numbers) => {
  if(rowKey === "min"){
    return numbers?.length > 0 ? Math.min(...numbers) : '-';
  } else if(rowKey === "max"){
    return numbers?.length > 0 ? Math.max(...numbers) : '-';
  } else if(rowKey === "avg" || rowKey === "avgActive" || rowKey === "deviation" || rowKey === "deviationActive"){
    return getAvgAndDeviationsForNumbers(numbers, rowKey);
  } else {
    return '-';
  }
}

export const getAgeNumberStatistics = (rowKey, numbers, patientsLength) => {
  if(!numbers || numbers?.length === 0) {
    return '-';
  }

  if(rowKey === "min"){
    return Math.min(...numbers);
  } else if(rowKey === "max"){
    return Math.max(...numbers);
  } else if(rowKey === "avg" || rowKey === "avgActive" || rowKey === "deviation" || rowKey === "deviationActive"){
    const calcLength = rowKey === "avg" || rowKey === "deviation" ? patientsLength : numbers.length;

    return (rowKey === "avg" || rowKey === "avgActive")
      ? Math.trunc(numbers.reduce((sum, elem) => sum + Number(elem), 0) / calcLength)
      : calcDeviation(numbers, calcLength);
  } else {
    return '-';
  }
}

export const getDatesStatistics = (rowKey, dates, entriesLength) => {
  const minMaxDates = getMinMaxDates(dates);

  if(minMaxDates?.length > 0){
    if(rowKey === "min"){
      return minMaxDates[0];
    } else if(rowKey === "max"){
      return minMaxDates[1];
    } /*else if(rowKey === "avg" || rowKey === "avgActive" || rowKey === "deviation" || rowKey === "deviationActive"){
      return getAvgAndDeviationsForDates(dates, rowKey, entriesLength);
    } */else {
      return '-';
    }
  }
}

export const getIsDarkColumn = (header) => {
  return header === Consts.PATIENT || header === Consts.GENDER || header === Consts.YEAR_OF_BIRTH
    || header === Consts.LAST_LOGIN || header === Consts.NUMBER_LOGINS || header === Consts.LAST_DISEASE_DAYS
    || header === Consts.PGX_LAST_CHANGE || header === Consts.NUMBER_ACTIONABLE_GENES
    || header === Consts.NUMBER_DRUGS_INITIAL || header === Consts.NUMBER_NEW_ADDED_DRUGS
    || header === Consts.NUMBER_MED_PLAN_CHANGES
    || header === Consts.NUMBER_DGIS || header === Consts.INCREASED_RISK_LESS_10
    || header === Consts.INCREASED_RISK_FROM_20_TO_30 || header === Consts.INCREASED_RISK_FROM_40_TO_50
    || header === Consts.COMBINED_RISK_LESS_10 || header === Consts.COMBINED_RISK_FROM_20_TO_30
    || header === Consts.COMBINED_RISK_FROM_40_TO_50 || header === Consts.NUMBER_PDMS
    || header === Consts.NUMBER_SYMPTOMS || header === Consts.NUMBER_OTHER_SYMPTOMS
    || header === Consts.NUMBER_LAB_TESTS || header === Consts.LAST_MEASUREMENT_DAYS;
}

export const getColumnWidth = (header, index) => {
  if (index === 0 || header === Consts.STUDY_ARM) {
    return "200px";
  }

  if (header === Consts.GENDER) {
    return "72px";
  }

  if (header === Consts.REG_AGE || header === Consts.YEAR_OF_BIRTH || header === Consts.MED_PLAN_LAST_CHANGE) {
    return "110px";
  }

  if (header === Consts.REGISTRATION_DATE || header === Consts.LAST_LOGIN) {
    return "130px";
  }

  if ([
    Consts.DISEASES_LAST_CHANGE,
    Consts.PGX_LAST_CHANGE,
    Consts.SYMPTOMS_LAST_CHANGE,
    Consts.LAB_TESTS_LAST_CHANGE,
    Consts.MEASUREMENTS_LAST_CHANGE,
    Consts.NUMBER_DDGIS,
  ].includes(header)) {
    return "170px";
  }

  if (!header) {
    return "84px";
  }

  return "100px";
};

export const getScrollablePanelStyle = (rowCount, maxVisibleRows, hasHeader = false, isExpanded = true) => {
  return isExpanded
    ? undefined
    : {
        minHeight: "0",
        maxHeight: "0",
        overflow: "hidden",
      }
};

export const getTableScrollStyle = (rowCount, maxVisibleRows, hasHeader = false) => {
  const rowHeight = 52;
  const headerHeight = hasHeader ? 52 : 0;
  const reservedHeight = headerHeight + (maxVisibleRows * rowHeight);

  return rowCount <= maxVisibleRows
    ? undefined
    : {
      height: `${reservedHeight}px`,
      overflowY: "auto",
      overflowX: "scroll",
    }
}

export const getTablesSectionMinHeight = () => {
  const rowHeight = 52;
  const toggleHeight = 56;
  const firstTableHeight = 52 + (6 * rowHeight);
  const secondTableHeight = 5 * rowHeight;
  return `${(2 * toggleHeight) + firstTableHeight + secondTableHeight}px`;
};

export const calcAge = (regDate, yearBirth) => {
  if (!yearBirth || yearBirth === "1900" || yearBirth === "-") {
    return '-';
  }

  const reg = regDate ? new Date(regDate) : new Date();
  return reg.getFullYear() - Number.parseInt(yearBirth);
}

export const areDateStatFields = (header) => {
  return header === Consts.REGISTRATION_DATE || header === Consts.LAST_LOGIN
    || header === Consts.DISEASES_LAST_CHANGE || header === Consts.PGX_LAST_CHANGE
    || header === Consts.SYMPTOMS_LAST_CHANGE || header === Consts.LAB_TESTS_LAST_CHANGE
    || header === Consts.MEASUREMENTS_LAST_CHANGE;
}

export const areNumberStatFields = (header) => {
  return header === Consts.REG_AGE || header === Consts.NUMBER_LOGINS || header === Consts.LAST_LOGIN_DAYS
    || header === Consts.LAST_DISEASE_DAYS || header === Consts.LAST_MEASUREMENT_DAYS
    || header === Consts.NUMBER_DISEASES
    || header === Consts.NUMBER_GENES || header === Consts.NUMBER_DRUGS || header === Consts.NUMBER_DRUGS_INITIAL
    || header === Consts.NUMBER_NEW_ADDED_DRUGS || header === Consts.NUMBER_DELETED_DRUGS
    || header === Consts.NUMBER_MED_PLAN_CHANGES || header === Consts.NUMBER_DDIS || header === Consts.NUMBER_DGIS
    || header === Consts.NUMBER_PDMS || header.indexOf("risk_combined") >= 0
    || header.indexOf("risk_relative") >= 0 || header === Consts.NUMBER_SYMPTOMS
    || header === Consts.NUMBER_DIFF_SYMPTOMS || header === Consts.NUMBER_OTHER_SYMPTOMS
    || header === Consts.NUMBER_LAB_TESTS || header === Consts.NUMBER_MEASUREMENTS || header === Consts.YEAR_OF_BIRTH;
}

export const areRegAndLoginDatesForAvgAndDeviation = (header, rowKey) => {
  return (header === Consts.REGISTRATION_DATE || header === Consts.LAST_LOGIN)
    && (rowKey === "avg" || rowKey === "avgActive" || rowKey === "deviation" || rowKey === "deviationActive");
}

export function buildPatIdsArrayForDdgis(origArray) {
  // Creating a unique key for compare
  const createKey = (item) => {
    const atcs = [...item?.ddgis?.atcs]?.sort();
    const geneInfos = [...item?.ddgis?.geneInfos]?.sort();
    return JSON.stringify({atcs, geneInfos});
  };

  // Group patientIds by equal content
  const groups = new Map();

  for (const item of origArray) {
    const key = createKey(item);

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key).push(item.patientId);
  }

  // For each original element, return the corresponding group
  //return origArray.map(item => groups.get(createKey(item)));
  return origArray.map(item => {
    const group = groups.get(createKey(item));

    return [
      item.patientId,
      ...group.filter(id => id !== item.patientId)
    ];
  });
}

export const daysBetween = (date1, date2) => {
  const invalidValues = [null, undefined, "", "-"];

  if (invalidValues.includes(date1) || invalidValues.includes(date2)) {
    return 0;
  }

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  if (isNaN(d1) || isNaN(d2)) {
    return 0;
  }

  return Math.floor(Math.abs(d2 - d1) / 86400000);
}
